import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, Trash2, Store, Clock, CalendarX, MessageCircle, Image as ImageIcon } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useSettings } from "../../contexts/SettingsContext";
import ImageUploader from "../../components/ImageUploader";

export default function AdminSettings() {
  const { refresh } = useSettings();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [closure, setClosure] = useState({ date: "", reason: "" });

  useEffect(() => {
    api.get("/admin/settings").then((r) => setForm(r.data)).catch(() => toast.error("Chargement impossible"));
  }, []);

  if (!form) return <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-gold" /></div>;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setHour = (day, k, v) =>
    setForm((f) => ({ ...f, hours: f.hours.map((h) => (h.day === day ? { ...h, [k]: v } : h)) }));

  const addClosure = () => {
    if (!closure.date) { toast.error("Choisissez une date"); return; }
    set("closures", [...(form.closures || []), closure]);
    setClosure({ date: "", reason: "" });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/settings", form);
      toast.success("Réglages enregistrés");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl" data-testid="admin-settings-page">
      <h1 className="font-display text-2xl font-extrabold text-slate-900">Réglages du magasin</h1>

      <div className="card-oxy mt-5 space-y-4 p-5">
        <h2 className="flex items-center gap-2 font-display font-bold text-slate-900"><Store className="h-5 w-5 text-gold-dark" /> Informations</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="label-oxy">Nom du magasin</label>
            <input data-testid="set-store-name" value={form.store_name} onChange={(e) => set("store_name", e.target.value)} className="input-oxy" />
          </div>
          <div>
            <label className="label-oxy">Slogan</label>
            <input data-testid="set-tagline" value={form.tagline} onChange={(e) => set("tagline", e.target.value)} className="input-oxy" />
          </div>
          <div>
            <label className="label-oxy">Téléphone</label>
            <input data-testid="set-phone" type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-oxy" placeholder="06 12 34 56 78" />
          </div>
          <div>
            <label className="label-oxy">Email</label>
            <input data-testid="set-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-oxy" placeholder="contact@…" />
          </div>
          <div>
            <label className="label-oxy">Adresse</label>
            <input data-testid="set-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="input-oxy" placeholder="12 rue de la Paix" />
          </div>
          <div>
            <label className="label-oxy">Ville</label>
            <input data-testid="set-city" value={form.city} onChange={(e) => set("city", e.target.value)} className="input-oxy" placeholder="Paris 75011" />
          </div>
        </div>
        <div>
          <label className="label-oxy">Adresse pour Google Maps (carte + itinéraire)</label>
          <input data-testid="set-maps-address" value={form.maps_address} onChange={(e) => set("maps_address", e.target.value)} className="input-oxy" placeholder="12 rue de la Paix, 75011 Paris" />
        </div>
        <div>
          <label className="label-oxy">Lien de votre fiche Google (bouton « Voir nos avis Google »)</label>
          <input data-testid="set-google-url" type="url" value={form.google_reviews_url} onChange={(e) => set("google_reviews_url", e.target.value)} className="input-oxy" placeholder="https://g.page/…" />
        </div>
      </div>

      <div className="card-oxy mt-5 space-y-4 p-5">
        <h2 className="flex items-center gap-2 font-display font-bold text-slate-900"><MessageCircle className="h-5 w-5 text-gold-dark" /> WhatsApp & notifications</h2>
        <div>
          <label className="label-oxy">Numéro WhatsApp (format international, ex : 33612345678)</label>
          <input data-testid="set-whatsapp" type="tel" value={form.whatsapp_number} onChange={(e) => set("whatsapp_number", e.target.value)} className="input-oxy" placeholder="33612345678" />
          <p className="mt-1 text-xs text-slate-400">Active le bouton flottant et tous les boutons WhatsApp du site.</p>
        </div>
        <div>
          <label className="label-oxy">Email de notification (nouvelles réservations, devis, RDV)</label>
          <input data-testid="set-notif-email" type="email" value={form.notification_email} onChange={(e) => set("notification_email", e.target.value)} className="input-oxy" placeholder="vous@email.fr" />
        </div>
      </div>

      <div className="card-oxy mt-5 space-y-4 p-5">
        <h2 className="flex items-center gap-2 font-display font-bold text-slate-900"><ImageIcon className="h-5 w-5 text-gold-dark" /> Logo & photo de vitrine</h2>
        <div>
          <label className="label-oxy">Logo (affiché en haut du site et dans le pied de page)</label>
          <ImageUploader images={form.logo_image ? [form.logo_image] : []} onChange={(v) => set("logo_image", v[0] || "")} max={1} />
        </div>
        <div>
          <label className="label-oxy">Photo de vitrine (grande image d'accueil)</label>
          <ImageUploader images={form.hero_image ? [form.hero_image] : []} onChange={(v) => set("hero_image", v[0] || "")} max={1} />
        </div>
        <label className="flex items-center gap-3 rounded-xl bg-slate-50 p-3.5">
          <input type="checkbox" data-testid="set-hide-soldout" checked={form.hide_sold_out} onChange={(e) => set("hide_sold_out", e.target.checked)}
            className="h-5 w-5 accent-[#D4AF37]" />
          <span className="text-sm font-semibold text-slate-700">Masquer automatiquement les produits épuisés / vendus</span>
        </label>
      </div>

      <div className="card-oxy mt-5 p-5">
        <h2 className="flex items-center gap-2 font-display font-bold text-slate-900"><Clock className="h-5 w-5 text-gold-dark" /> Horaires d'ouverture</h2>
        <div className="mt-4 space-y-3">
          {form.hours.map((h) => (
            <div key={h.day} data-testid={`hours-row-${h.day}`} className="rounded-2xl border border-slate-200 p-3.5">
              <div className="flex items-center justify-between">
                <span className="font-display font-bold capitalize text-slate-900">{h.day}</span>
                <button type="button" data-testid={`hours-toggle-${h.day}`} onClick={() => setHour(h.day, "open", !h.open)}
                  className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${h.open ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-500"}`}>
                  {h.open ? "Ouvert" : "Fermé"}
                </button>
              </div>
              {h.open && (
                <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500">Ouverture</label>
                    <input type="time" data-testid={`hours-start-${h.day}`} value={h.start} onChange={(e) => setHour(h.day, "start", e.target.value)} className="input-oxy !py-2" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500">Fermeture</label>
                    <input type="time" data-testid={`hours-end-${h.day}`} value={h.end} onChange={(e) => setHour(h.day, "end", e.target.value)} className="input-oxy !py-2" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500">Pause début</label>
                    <input type="time" data-testid={`hours-break-start-${h.day}`} value={h.break_start || ""} onChange={(e) => setHour(h.day, "break_start", e.target.value)} className="input-oxy !py-2" />
                  </div>
                  <div>
                    <label className="text-[11px] font-semibold text-slate-500">Pause fin</label>
                    <input type="time" data-testid={`hours-break-end-${h.day}`} value={h.break_end || ""} onChange={(e) => setHour(h.day, "break_end", e.target.value)} className="input-oxy !py-2" />
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="card-oxy mt-5 p-5">
        <h2 className="flex items-center gap-2 font-display font-bold text-slate-900"><CalendarX className="h-5 w-5 text-gold-dark" /> Fermetures exceptionnelles</h2>
        <div className="mt-3 flex flex-col gap-2 sm:flex-row">
          <input type="date" data-testid="closure-date" value={closure.date} onChange={(e) => setClosure((c) => ({ ...c, date: e.target.value }))} className="input-oxy sm:w-44" />
          <input data-testid="closure-reason" value={closure.reason} onChange={(e) => setClosure((c) => ({ ...c, reason: e.target.value }))}
            placeholder="Motif (ex : congés)" className="input-oxy flex-1" />
          <button type="button" data-testid="closure-add-btn" onClick={addClosure} className="btn-ink !px-4 !py-3 text-sm"><Plus className="h-4 w-4" /> Ajouter</button>
        </div>
        <ul className="mt-3 space-y-2">
          {(form.closures || []).map((c, i) => (
            <li key={i} className="flex items-center justify-between rounded-xl bg-slate-50 px-4 py-2.5 text-sm">
              <span><strong>{c.date}</strong> — {c.reason || "Fermé"}</span>
              <button data-testid={`closure-delete-${i}`} onClick={() => set("closures", form.closures.filter((_, j) => j !== i))}
                className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      </div>

      <div className="sticky bottom-3 z-10 mt-6 rounded-2xl border border-slate-200 bg-white/95 p-3 shadow-xl backdrop-blur">
        <button data-testid="settings-save-btn" onClick={save} disabled={saving} className="btn-gold w-full !py-3 disabled:opacity-60">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {saving ? "Enregistrement…" : "Enregistrer tous les réglages"}
        </button>
      </div>
    </div>
  );
}
